/**
 * TextArea Component
 *
 * A multi-line text input component.
 */

import React, { forwardRef, useState } from 'react';
import './Input.css';
import './TextArea.css';

const TextArea = forwardRef(({
  size = 'md',
  label,
  placeholder,
  value,
  defaultValue,
  onChange,
  onBlur,
  onFocus,
  disabled = false,
  readOnly = false,
  error,
  helperText,
  fullWidth = false,
  required = false,
  rows = 4,
  maxLength,
  showCount = false,
  resize = 'vertical',
  id,
  name,
  className = '',
  ...props
}, ref) => {
  const [isFocused, setIsFocused] = useState(false);
  const [charCount, setCharCount] = useState(
    (value || defaultValue || '').length
  );

  const handleFocus = (e) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  const handleChange = (e) => {
    setCharCount(e.target.value.length);
    onChange?.(e);
  };

  const containerClassNames = [
    'ds-input',
    'ds-textarea',
    `ds-input--${size}`,
    fullWidth && 'ds-input--full-width',
    disabled && 'ds-input--disabled',
    error && 'ds-input--error',
    isFocused && 'ds-input--focused',
    className,
  ].filter(Boolean).join(' ');

  const inputId = id || name;

  return (
    <div className={containerClassNames}>
      {label && (
        <label htmlFor={inputId} className="ds-input__label">
          {label}
          {required && <span className="ds-input__required">*</span>}
        </label>
      )}

      <div className="ds-textarea__wrapper">
        <textarea
          ref={ref}
          id={inputId}
          name={name}
          value={value}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          rows={rows}
          maxLength={maxLength}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className="ds-textarea__field"
          style={{ resize }}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={
            error || helperText ? `${inputId}-helper` : undefined
          }
          {...props}
        />

        {showCount && maxLength && (
          <div className="ds-textarea__count">
            {charCount} / {maxLength}
          </div>
        )}
      </div>

      {(error || helperText) && (
        <div
          id={`${inputId}-helper`}
          className={`ds-input__helper ${error ? 'ds-input__helper--error' : ''}`}
        >
          {error || helperText}
        </div>
      )}
    </div>
  );
});

TextArea.displayName = 'TextArea';

export default TextArea;
